import { FC } from "react";
import {
  AutoReassignIntervalUnit,
  AutoReassignIntervalSourceDateType,
  AutomationType,
} from "@src/types.generated";

// Modern shadcn components
import { Switch } from "@src/ui/switch";
import { Input } from "@src/ui/input";
import { Label } from "@src/ui/label";
import { RadioGroup, RadioGroupItem } from "@src/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/ui/select";
import { Separator } from "@src/ui/separator";
import { Card, CardContent } from "@src/ui/card";
import Text from "@src/ui/text";
import { InfoCircledIcon } from "@radix-ui/react-icons";

type Props = {
  automationType: AutomationType;
  autoReassign: boolean;
  autoReassignIntervalUnits?: AutoReassignIntervalUnit | null;
  autoReassignIntervalCount?: number | null;
  autoReassignSourceDate?: AutoReassignIntervalSourceDateType | null;
  onAutoReassignChange: (value: boolean) => void;
  onIntervalUnitsChange: (value: AutoReassignIntervalUnit) => void;
  onIntervalCountChange: (value: number) => void;
  onSourceDateChange: (value: AutoReassignIntervalSourceDateType) => void;
  missingIntervalValidationError?: boolean;
  missingCountValidationError?: boolean;
  showReassignmentWarning?: boolean;
};

const INTERVAL_UNIT_OPTIONS = [
  { value: AutoReassignIntervalUnit.Day, label: "days" },
  { value: AutoReassignIntervalUnit.Week, label: "weeks" },
  { value: AutoReassignIntervalUnit.Month, label: "months" },
  { value: AutoReassignIntervalUnit.Year, label: "years" },
];

const SOURCE_DATE_OPTIONS = [
  { value: AutoReassignIntervalSourceDateType.Assignment, label: "Assignment" },
  { value: AutoReassignIntervalSourceDateType.Completion, label: "Completion" },
];

const AutoReassignmentConfig: FC<Props> = ({
  automationType,
  autoReassign,
  autoReassignIntervalUnits,
  autoReassignIntervalCount,
  autoReassignSourceDate,
  onAutoReassignChange,
  onIntervalUnitsChange,
  onIntervalCountChange,
  onSourceDateChange,
  missingIntervalValidationError = false,
  missingCountValidationError = false,
  showReassignmentWarning = false,
}) => {
  // Only show for assignment automations
  if (automationType !== AutomationType.Assignment) {
    return null;
  }

  // Validation logic - using specific boolean props
  const hasCountError = missingCountValidationError;
  const hasIntervalError = missingIntervalValidationError;

  return (
    <div className="space-y-6 self-stretch">
      <Separator />

      {/* Main toggle section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Text type="P1" fontWeight="SemiBold">
            Auto reassign
          </Text>
          <Switch
            data-testid="auto-reassign-toggle"
            checked={autoReassign}
            onCheckedChange={onAutoReassignChange}
          />
        </div>
        <Text type="P2" className="text-muted-foreground" multiline>
          Automatically reassign based on time intervals.
        </Text>
      </div>

      {/* Configuration when enabled */}
      {autoReassign && (
        <Card>
          <CardContent className="space-y-6 p-4">
            {/* Interval configuration */}
            <div className="space-y-2">
              <Text type="P2" fontWeight="Medium">
                Reassign every
              </Text>
              <div className="flex items-center gap-3">
                <Input
                  data-testid="auto-reassign-interval-count"
                  type="number"
                  value={autoReassignIntervalCount?.toString() || ""}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    onIntervalCountChange(value);
                  }}
                  min={1}
                  placeholder="Count"
                  inputClassName={hasCountError ? "border border-red-5" : ""}
                  className={`w-32`}
                />
                <Select
                  value={autoReassignIntervalUnits || ""}
                  onValueChange={(value) =>
                    onIntervalUnitsChange(
                      (value as AutoReassignIntervalUnit) || null,
                    )
                  }
                >
                  <SelectTrigger
                    className={`flex-1 ${hasIntervalError ? "border border-red-5" : ""}`}
                    data-testid="auto-reassign-interval-units"
                  >
                    <SelectValue placeholder="Select interval" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_UNIT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Source date configuration */}
            <div className="space-y-2">
              <Text type="P2" fontWeight="Medium">
                After
              </Text>
              <RadioGroup
                value={autoReassignSourceDate || ""}
                onValueChange={(value) =>
                  onSourceDateChange(
                    (value as AutoReassignIntervalSourceDateType) ||
                      AutoReassignIntervalSourceDateType.Assignment,
                  )
                }
              >
                {SOURCE_DATE_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2"
                    data-testid={`auto-reassign-source-${option.value.toLowerCase()}`}
                  >
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label
                      htmlFor={option.value}
                      className="cursor-pointer text-sm font-normal"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Warning when configuration changes have been made */}
            {showReassignmentWarning && (
              <div className="flex items-start gap-3 rounded-lg bg-blue-1 p-3">
                <InfoCircledIcon className="mt-0.5 size-4 shrink-0 text-blue-9" />
                <Text type="P2" className="text-blue-10" multiline>
                  Changes can cause immediate re-assignment to past completed
                  assignments which meet the new criteria but not the old.
                </Text>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AutoReassignmentConfig;
